import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { FaqAccordion, FaqItem } from "./FaqAccordion";

export function useFaq(audience?: string) {
  return useQuery<{ items: FaqItem[] }>({ queryKey: ["faq", audience || "all"], queryFn: () => api.get("/faq", { params: audience ? { audience } : {} }).then((r) => r.data), staleTime: 300000 });
}

export function FaqWidget({ audience, eyebrow = "Good to know", title = "Your questions answered", limit = 6, tone = "wine" }: { audience: "cellar" | "club" | "gift"; eyebrow?: string; title?: string; limit?: number; tone?: "wine" | "cellar" }) {
  const { data } = useFaq(audience);
  const items = (data?.items || []).slice(0, limit);
  if (!items.length) return null;
  const colour = tone === "cellar" ? "var(--cellar-dark)" : "var(--club-dark)";
  return (
    <section className="mt-16 grid gap-8 border border-border p-6 sm:p-8 lg:grid-cols-12 lg:p-10" data-testid={`faq-widget-${audience}`}>
      <div className="lg:col-span-4">
        <div className="eyebrow" style={{ color: colour }}>{eyebrow}</div>
        <h2 className="mt-3 font-serif text-2xl sm:text-3xl" data-testid={`faq-widget-title-${audience}`}>{title}</h2>
        <p className="mt-3 text-sm leading-relaxed text-neutral-600">Straight answers from the team. Still unsure? <Link to="/contact" className="underline underline-offset-4 hover:text-[var(--wine)]">Contact us</Link>.</p>
        <Link to={`/faq?topic=${audience}`} className="group mt-6 inline-flex items-center gap-1 font-brand text-[12px] font-bold uppercase tracking-[0.12em]" style={{ color: colour }} data-testid={`faq-widget-more-${audience}`}>Read the full FAQ <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" /></Link>
      </div>
      <div className="lg:col-span-8"><FaqAccordion items={items} testId={`faq-widget-accordion-${audience}`} /></div>
    </section>
  );
}
